import {
  MAPBOX_ACCESS_TOKEN as LEGACY_MAPBOX_ACCESS_TOKEN,
  MAPBOX_DEFAULT_LNG_LAT,
  MAPBOX_DEFAULT_ZOOM,
  isOfflineOrDnsMapLoadingError,
  isPerTileMapLoadingError,
  isStyleLevelMapLoadingError,
  logIntermittentMapTileLoadMiss,
  MAP_STYLE_LOAD_TIMEOUT_MS,
  MAPBOX_OPERATOR_SEARCH_STYLE_URL,
  REGISTRATION_PROFILE_JSON_KEY,
  analyticsGeoCoordPair,
  featureHasPipelineProps,
  fetchCountyStatePairsReverseMapboxLngLat,
  formatStyleLevelMapLoadFailureMessage,
  FRIENDLY_CONNECTIVITY_MESSAGE,
  parseMapboxLngLat,
  parseMapboxLngLatFromRecord,
  parseProfileJurisdiction,
  resolvePipelineMapTapAllowed,
  AppEventTypes,
} from '@buxus/shared'
import type { BuxusOperatorPayload, CountyStatePair, MapLoadFailureCause, PipelineTapGateResolution } from '@buxus/shared'
import type { Feature, Geometry, LineString, MultiLineString } from 'geojson'
import mapboxgl from 'mapbox-gl'
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import 'mapbox-gl/dist/mapbox-gl.css'
import type { PipelineMapOperatorDirectoryEntry } from './mapPipelinePopupDom'
import {
  PIPELINE_MAP_JURISDICTION_DENIED_SHOW_LOCATION,
  PIPELINE_MAP_SHOW_JURISDICTION_DENIED_POPUP,
} from './mapPipelineConfig'
import { MapPipelineBubble } from './MapPipelineBubble'
import { MapPipelineJurisdictionDeniedBubble } from './MapPipelineJurisdictionDeniedBubble'
import { MapAddressSearchModal } from './components/map/MapAddressSearchModal'
import { MapCustomControls } from './components/map/MapCustomControls'
import { MapPickerOverlay } from './components/map/MapPickerOverlay'
import type { MapPickerConfig } from './components/map/mapPickerTypes'
import { useLockPageScrollWhileOpen } from './platform/ui/useLockPageScrollWhileOpen'
import { reportOperationalFailure, reportReliabilityFailure } from './platform/errors/webFailureReporting'
import {
  trackButtonPressed,
  trackMapAddressSearchSubmitted,
  trackMapPopupDisplayed,
  trackMapTapped,
  trackMenuItemSelected,
  formatLocationCountyState,
  type MapAddressSearchResult,
  type ScreenInteractionContext,
} from './platform/analytics/interactionAnalytics'

export type { MapPickerConfig, MapPickerItem } from './components/map/mapPickerTypes'

const HL_SRC = 'buxus-pipeline-highlight-src'
const HL_LAYER = 'buxus-pipeline-highlight-line'
/** Highlights via the pipeline vector tile source + filter (Mapbox GL JS pattern; no geometry copy). */
const HL_LAYER_VECTOR = 'buxus-pipeline-highlight-vector'

/** Mobile `Map.js` uses `turf.buffer(point, 40, { units: 'meters' })` before `queryRenderedFeaturesInRect`. */
const PIPELINE_QUERY_BUFFER_METERS = 40

function isLineGeometry(g: Geometry | null | undefined): g is LineString | MultiLineString {
  return g?.type === 'LineString' || g?.type === 'MultiLineString'
}

function MapLoadNotice({ message }: { message: string }) {
  return (
    <div className="map-load-notice" role="status" aria-live="polite">
      <p className="map-load-notice-text">{message}</p>
    </div>
  )
}

/** Geographic bbox (west, south, east, north) approximating a meter buffer around a point. */
function meterBufferLngLatBbox(lng: number, lat: number, meters: number): [number, number, number, number] {
  const latRad = (lat * Math.PI) / 180
  const dLat = meters / 111_320
  const dLng = meters / (111_320 * Math.cos(latRad))
  return [lng - dLng, lat - dLat, lng + dLng, lat + dLat]
}

function screenBboxFromLngLatBbox(
  map: mapboxgl.Map,
  west: number,
  south: number,
  east: number,
  north: number,
): [mapboxgl.PointLike, mapboxgl.PointLike] {
  const sw = map.project([west, south])
  const ne = map.project([east, north])
  const minX = Math.min(sw.x, ne.x)
  const maxX = Math.max(sw.x, ne.x)
  const minY = Math.min(sw.y, ne.y)
  const maxY = Math.max(sw.y, ne.y)
  return [
    [minX, minY],
    [maxX, maxY],
  ]
}

function styleLineLayerIds(map: mapboxgl.Map): string[] {
  const layers = map.getStyle()?.layers
  if (!layers) return []
  return layers.filter((l) => l.type === 'line').map((l) => l.id)
}

/** Query rendered features using the same ~40 m search area as mobile. */
function queryPipelineRenderedFeatures(
  map: mapboxgl.Map,
  lngLat: mapboxgl.LngLat,
  meters = PIPELINE_QUERY_BUFFER_METERS,
): mapboxgl.GeoJSONFeature[] {
  const [west, south, east, north] = meterBufferLngLatBbox(lngLat.lng, lngLat.lat, meters)
  const bbox = screenBboxFromLngLatBbox(map, west, south, east, north)

  const lineLayerIds = styleLineLayerIds(map)
  if (lineLayerIds.length > 0) {
    try {
      const lineHits = map.queryRenderedFeatures(bbox, {
        layers: lineLayerIds,
      }) as mapboxgl.GeoJSONFeature[]
      const pipelineLineHits = lineHits.filter(featureHasPipelineProps)
      if (pipelineLineHits.length > 0) return pipelineLineHits
    } catch {
      /* fall through to unfiltered query */
    }
  }

  try {
    return map.queryRenderedFeatures(bbox, {}) as mapboxgl.GeoJSONFeature[]
  } catch {
    return []
  }
}

function whenMapStyleReady(map: mapboxgl.Map, fn: () => void): () => void {
  let cancelled = false
  const run = (): void => {
    if (cancelled) return
    if (!map.isStyleLoaded()) {
      map.once('style.load', run)
      return
    }
    fn()
  }
  if (map.loaded()) run()
  else map.once('load', run)
  return () => {
    cancelled = true
  }
}

function queryHitLineGeometry(
  hit: mapboxgl.GeoJSONFeature,
): Feature<LineString | MultiLineString>['geometry'] | undefined {
  if (typeof hit.toJSON === 'function') {
    const g = hit.toJSON().geometry
    if (isLineGeometry(g)) {
      return cloneFeatureGeometry(g) as Feature<LineString | MultiLineString>['geometry']
    }
  }
  const g = hit.geometry
  if (isLineGeometry(g)) {
    return cloneFeatureGeometry(g) as Feature<LineString | MultiLineString>['geometry']
  }
  return undefined
}

/** Popup row may come from a symbol/fill hit; attach line geometry from a sibling line-layer hit (mobile ShapeSource needs lines). */
function enrichPipelineFeatureWithLineGeometry(
  feature: Feature,
  queryHits: mapboxgl.GeoJSONFeature[],
): Feature {
  if (isLineGeometry(feature.geometry)) return feature

  const props = (feature.properties ?? {}) as Record<string, unknown>
  const wantId = props.BUXUS_ID ?? props.buxus_id ?? props.Id
  const wantCo = props.CO_NAME ?? props.co_name

  for (const hit of queryHits) {
    if (!featureHasPipelineProps(hit)) continue
    const hp = (hit.properties ?? {}) as Record<string, unknown>
    if (wantId != null && hp.BUXUS_ID !== wantId && hp.buxus_id !== wantId && hp.Id !== wantId) {
      continue
    }
    if (wantCo != null && hp.CO_NAME !== wantCo && hp.co_name !== wantCo) {
      continue
    }
    const line = queryHitLineGeometry(hit)
    if (line) return { ...feature, geometry: line }
  }

  for (const hit of queryHits) {
    if (hit.layer?.type !== 'line' || !featureHasPipelineProps(hit)) continue
    const line = queryHitLineGeometry(hit)
    if (line) return { ...feature, geometry: line }
  }

  return feature
}

/**
 * Same GeoJSON feature object mobile keeps from `queryRenderedFeaturesInRect` and passes to
 * `ShapeSource shape={feature}`. Mapbox GL JS returns class instances; `toJSON()` is that shape.
 */
function pipelineFeatureFromQueryHit(f: mapboxgl.GeoJSONFeature): Feature {
  if (typeof f.toJSON === 'function') {
    return f.toJSON() as Feature
  }
  const props = (f.properties ?? {}) as Record<string, unknown>
  const geometryClone = cloneFeatureGeometry(f.geometry)
  return {
    type: 'Feature',
    id: f.id,
    properties: { ...props },
    ...(geometryClone ? { geometry: geometryClone } : {}),
  } as Feature
}

function getToken(): string {
  const fromEnv = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
  if (typeof fromEnv === 'string' && fromEnv.trim().length > 0) return fromEnv.trim()
  return LEGACY_MAPBOX_ACCESS_TOKEN
}

function parseNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const t = value.trim()
    if (t === '') return null
    const n = Number(t)
    if (Number.isFinite(n)) return n
  }
  return null
}

function isLikelyMapboxStyleUrl(v: string): boolean {
  const s = v.trim()
  return s.startsWith('mapbox://styles/') || s.startsWith('https://')
}

function readPath(obj: unknown, key: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined
  return (obj as Record<string, unknown>)[key]
}

/**
 * Published stakeholder blobs merge map settings onto `operator` at publish time
 * (see BuxusManager stakeholder publish). Mobile uses `currentOperator.mapboxLink` only.
 */
function readPublishedOperatorMapStyleUrl(
  operator: Record<string, unknown> | undefined,
): string | null {
  const raw =
    readPath(operator, 'mapboxLink') ??
    readPath(operator, 'MapboxLink') ??
    readPath(operator, 'mapStyleUrl') ??
    readPath(operator, 'MapStyleUrl')
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (!trimmed || !isLikelyMapboxStyleUrl(trimmed)) return null
  return trimmed
}

function collectAssetRows(payload: BuxusOperatorPayload): Record<string, unknown>[] {
  const op = payload.operator as Record<string, unknown> | undefined
  const fromOp = op?.assets ?? op?.Assets
  if (Array.isArray(fromOp)) return fromOp as Record<string, unknown>[]
  const root = payload as unknown as Record<string, unknown>
  return Array.isArray(root.assets) ? (root.assets as Record<string, unknown>[]) : []
}

function findAssetRowById(
  payload: BuxusOperatorPayload | undefined,
  assetId: string | null | undefined,
): Record<string, unknown> | null {
  if (!payload || !assetId?.trim()) return null
  const want = assetId.trim()
  for (const row of collectAssetRows(payload)) {
    const id = row.id ?? row.Id
    if (typeof id === 'string' && id.trim() === want) return row
  }
  return null
}

export type MapRole = 'embeddedOperator' | 'explorerHome' | 'hazardStorage'

/** Fly-to target when user picks an asset from the All Assets map rail. */
export type MapAssetFocus = {
  assetId: string
  center: [number, number]
  zoom: number
  popupTitle: string
  popupDetailLines: string[]
  openPopup?: boolean
  popupMinimal?: boolean
  onOpenDetail?: () => void
  focusGeneration?: number
}

export type HazardMapView = {
  styleUrl: string
  center: [number, number]
  zoom: number
  markerLabel: string
}

type MapProps = {
  payload?: BuxusOperatorPayload
  stakeholderCode?: string | null
  activeAssetId?: string | null
  embedded?: boolean
  /** When set and non-empty, pipeline vector popups require a reverse‑geocode jurisdiction match. */
  jurisdictionCountyStateKeys?: ReadonlySet<string> | null
  mapRole?: MapRole
  /** Required when `mapRole === 'hazardStorage'` (asset style + storage coordinates). */
  hazardMapView?: HazardMapView | null
  /** One-shot center/zoom from Key Locations lat/long link (mobile `goToMap` parity). */
  mapViewOverride?: { center: [number, number]; zoom: number } | null
  /** Downloaded-operator list for legacy “View Operator” parity (ZOHO_OPERA vs CO_NAME). */
  pipelineOperatorDirectory?: readonly PipelineMapOperatorDirectoryEntry[] | undefined
  onViewPipelineOperator?: ((operatorId: string) => void) | undefined
  /** Title + center/zoom status row (includes divider under header). Default off. */
  showMapHeader?: boolean
  /** Overrides default title (`Pipeline map` on explorer home, otherwise `Map`). */
  mapHeaderTitle?: string | null
  /** Semi-translucent overlay picker (home operators; operator overview assets). */
  mapPicker?: MapPickerConfig | null
  /** When true, skip operator default framing while a rail asset fly-to is active. */
  suppressOperatorFraming?: boolean
  /** Synchronous lock set in rail click handler before parent re-render. */
  operatorFramingLockRef?: RefObject<boolean>
  /** Notified when the Mapbox instance is created or torn down. */
  onMapInstance?: (map: mapboxgl.Map | null) => void
  /** Fired when the map is clicked but no pipeline feature was hit (e.g. dismiss overlays). */
  onMapBackgroundClick?: () => void
  /** True while a pipeline segment or jurisdiction-denied popup is shown (suppress rail hover previews). */
  onPipelineOverlayActiveChange?: (active: boolean) => void
  analytics?: ScreenInteractionContext
}

function resolveMapView(
  payload: BuxusOperatorPayload | undefined,
  _stakeholderCode: string | null | undefined,
  activeAssetId: string | null | undefined,
  mapRole: MapRole,
  hazardMapView: HazardMapView | null | undefined,
): {
  styleUrl: string | null
  center: [number, number]
  zoom: number
  markerLabel: string
} {
  if (mapRole === 'explorerHome') {
    return {
      styleUrl: MAPBOX_OPERATOR_SEARCH_STYLE_URL,
      center: MAPBOX_DEFAULT_LNG_LAT,
      zoom: MAPBOX_DEFAULT_ZOOM,
      markerLabel: 'Map center',
    }
  }

  if (mapRole === 'hazardStorage' && hazardMapView) {
    return { ...hazardMapView }
  }

  const operator = payload?.operator as unknown as Record<string, unknown> | undefined
  const styleUrl = readPublishedOperatorMapStyleUrl(operator)

  const rawLat =
    operator &&
    (parseNumber(readPath(operator, 'lat')) ?? parseNumber(readPath(operator, 'Lat')))
  const rawLng =
    operator &&
    (parseNumber(readPath(operator, 'long')) ?? parseNumber(readPath(operator, 'Long')))
  const rawZoom =
    operator &&
    (parseNumber(readPath(operator, 'zoomLevel')) ??
      parseNumber(readPath(operator, 'ZoomLevel')))

  const opCenter = parseMapboxLngLat(rawLat, rawLng, { rejectOrigin: true })

  const opZoom =
    rawZoom != null && Number.isFinite(rawZoom)
      ? Math.min(22, Math.max(0, rawZoom))
      : null

  const effectiveStyleUrl = styleUrl && opCenter ? styleUrl : null

  const assetRow = findAssetRowById(payload, activeAssetId)
  if (assetRow) {
    const rawAssetZoom =
      parseNumber(readPath(assetRow, 'zoomLevel')) ?? parseNumber(readPath(assetRow, 'ZoomLevel'))

    const markerLabel =
      (typeof assetRow.assetName === 'string' && assetRow.assetName.trim()) ||
      (typeof assetRow.AssetName === 'string' && assetRow.AssetName.trim()) ||
      (typeof assetRow.id === 'string' && assetRow.id.trim()) ||
      (typeof assetRow.Id === 'string' && assetRow.Id.trim()) ||
      'Asset location'

    const assetCenter = parseMapboxLngLatFromRecord(assetRow, { rejectOrigin: true })
    if (assetCenter) {
      const assetZoom =
        rawAssetZoom != null && Number.isFinite(rawAssetZoom)
          ? Math.min(22, Math.max(0, rawAssetZoom))
          : 12
      return {
        styleUrl: styleUrl && assetCenter ? styleUrl : null,
        center: assetCenter,
        zoom: assetZoom,
        markerLabel,
      }
    }

    return {
      styleUrl: effectiveStyleUrl,
      center: opCenter ?? MAPBOX_DEFAULT_LNG_LAT,
      zoom: opZoom ?? MAPBOX_DEFAULT_ZOOM,
      markerLabel,
    }
  }

  const opNameFallback =
    (typeof operator?.operatorName === 'string' && operator.operatorName.trim()) ||
    (typeof operator?.OperatorName === 'string' && operator.OperatorName.trim()) ||
    'Operator location'

  return {
    styleUrl: effectiveStyleUrl,
    center: opCenter ?? MAPBOX_DEFAULT_LNG_LAT,
    zoom: opZoom ?? MAPBOX_DEFAULT_ZOOM,
    markerLabel: opNameFallback,
  }
}

function readRegistrationProfileBlob(): unknown | null {
  try {
    const raw = localStorage.getItem(REGISTRATION_PROFILE_JSON_KEY)
    if (!raw?.trim()) return null
    return JSON.parse(raw) as unknown
  } catch {
    return null
  }
}

/** Sorted copy for stable console comparison. */
function jurisdictionKeysSorted(set: ReadonlySet<string> | null | undefined): string[] {
  if (!set?.size) return []
  return [...set].sort((a, b) => a.localeCompare(b))
}

type PipelineDenyReason = Extract<PipelineTapGateResolution, { allowed: false }>['reason']

function pipelineTapDenialDebugHint(reason: PipelineDenyReason): string {
  switch (reason) {
    case 'no_county_state':
      return 'Mapbox reverse geocode returned no district/region (county/state) pairs, so jurisdiction cannot be evaluated.'
    case 'no_profile_keys':
      return 'Profile is not unrestricted and no jurisdiction keys were available from localStorage or the registration profile blob.'
    case 'no_jurisdiction_match':
      return 'Reverse-geocoded place(s) did not match any key in the effective jurisdiction set.'
    default:
      return String(reason)
  }
}

/** Temporary: console-only aid for diagnosing jurisdiction denials without UI signal. */
function logPipelineTapDeniedDebug(args: {
  reason: PipelineDenyReason
  lngLat: { lng: number; lat: number }
  geoPairs: { county: string; state: string }[]
  profileKind: ReturnType<typeof parseProfileJurisdiction>['kind']
  storedKeyCount: number
  profileRestrictedKeyCount: number
  effectiveKeyCount: number
  effectiveKeySource: 'localStorage' | 'registrationProfile' | 'none'
  /** County–state keys from app localStorage (passed as `jurisdictionCountyStateKeys` prop). */
  jurisdictionKeysFromLocalStorage: string[]
  /** County–state keys parsed from cached registration profile when restricted. */
  jurisdictionKeysFromProfile: string[]
  /** Keys actually used by the gate (`localStorage` if non-empty, else profile restricted keys). */
  effectiveJurisdictionKeys: string[]
}): void {
  console.debug('[Buxus pipeline map] tap denied (jurisdiction gate)', {
    ...args,
    hint: pipelineTapDenialDebugHint(args.reason),
  })
}

const PIPELINE_CLICK_DEBUG_SUMMARY_LIMIT = 36

function buildPipelineTapDedupeKey(f: mapboxgl.GeoJSONFeature): string {
  const props = (f.properties ?? {}) as Record<string, unknown>
  const bid = props.BUXUS_ID ?? props.buxus_id ?? props.Id
  const geom = f.geometry
  const geomKey =
    geom && typeof geom === 'object' && 'coordinates' in geom
      ? JSON.stringify((geom as { coordinates: unknown }).coordinates)
      : 'x'
  return bid != null && String(bid).trim() ? `id:${String(bid)}` : `${f.layer?.id ?? '?'}:${geomKey}`
}

/** Counts rendered hits that match pipeline row schema vs unique rows after de-dupe (see `cloneQueryFeatures`). */
function pipelineQueryDedupeStats(raw: mapboxgl.GeoJSONFeature[]): {
  rawCount: number
  withPipelinePropsHitCount: number
  duplicatePipelineSkipCount: number
  uniquePipelineCloneCount: number
} {
  const seen = new Set<string>()
  let withPipelinePropsHitCount = 0
  let duplicatePipelineSkipCount = 0
  for (const f of raw) {
    if (!featureHasPipelineProps(f)) continue
    withPipelinePropsHitCount++
    const k = buildPipelineTapDedupeKey(f)
    if (seen.has(k)) duplicatePipelineSkipCount++
    else seen.add(k)
  }
  return {
    rawCount: raw.length,
    withPipelinePropsHitCount,
    duplicatePipelineSkipCount,
    uniquePipelineCloneCount: seen.size,
  }
}

function previewPipelineRelatedProps(props: Record<string, unknown>): Record<string, unknown> {
  const keys = [
    'CO_NAME',
    'PRODUCT',
    'PIPE_TYPE',
    'SYS_NAME',
    'BUXUS_ID',
    'ZOHO_OPERA',
    'OWNER',
    'EM_PHONE',
    'Shape_Leng',
  ] as const
  const out: Record<string, unknown> = {}
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(props, k)) {
      const v = props[k]
      if (v !== null && v !== undefined && v !== '') out[k] = v
    }
  }
  return out
}

function renderedHitDebugSummary(f: mapboxgl.GeoJSONFeature, index: number): Record<string, unknown> {
  const props = (f.properties ?? {}) as Record<string, unknown>
  const matchesPipelineRowSchema = featureHasPipelineProps(f)
  const src = (f as unknown as { source?: string }).source
  return {
    index,
    layerId: f.layer?.id ?? null,
    source: typeof src === 'string' ? src : null,
    sourceLayer: f.sourceLayer ?? null,
    geometryType: f.geometry?.type ?? null,
    featureIdentifier: f.id,
    matchesPipelineRowSchema,
    dedupeKeyPreview: matchesPipelineRowSchema ? buildPipelineTapDedupeKey(f) : null,
    pipelinePropsPreview: previewPipelineRelatedProps(props),
    nonPipelineNote: matchesPipelineRowSchema
      ? null
      : 'No non-empty value for any key in shared mapFeatureProps (e.g. CO_NAME, SYS_NAME, Shape_Leng …).',
  }
}

/** Logs every click on the pipeline-interactive map: query hits, de-dupe, and whether a popup will be attempted. */
function logPipelineMapClickDebug(args: {
  mapRole: MapRole
  lngLat: mapboxgl.LngLat
  zoom: number
  bearing: number
  pitch: number
  pointPx: { x: number; y: number }
  bboxPadPx: number
  renderedFeatures: mapboxgl.GeoJSONFeature[]
  clonedPipelineFeatures: Feature[]
  queryRenderedFeaturesError?: string | null
}): void {
  const { renderedFeatures: raw, clonedPipelineFeatures: selected } = args
  const dedupe = pipelineQueryDedupeStats(raw)

  const nonPipelineHitCount = dedupe.rawCount - dedupe.withPipelinePropsHitCount
  let nextStepHint: string
  if (args.queryRenderedFeaturesError) {
    nextStepHint =
      'queryRenderedFeatures threw — see query.queryRenderedFeaturesError; likely style or map state.'
  } else if (dedupe.rawCount === 0) {
    nextStepHint =
      'queryRenderedFeatures returned nothing in the click box — line may be outside query padding, below min zoom, filtered by layer, or style not fully loaded.'
  } else if (dedupe.uniquePipelineCloneCount === 0) {
    nextStepHint =
      nonPipelineHitCount > 0
        ? 'Hits exist but none carry shared mapFeatureProps keys; likely not the pipeline vector layer (or different property names).'
        : 'Unexpected: no pipeline-shaped rows after query (check featureHasPipelineProps / tile schema).'
  } else {
    nextStepHint =
      'Pipeline-like feature(s) found; follow-up log line shows jurisdiction reverse-geocode and gate result.'
  }

  console.debug('[Buxus pipeline map] click (all hits under cursor)', {
    mapRole: args.mapRole,
    lngLat: { lng: args.lngLat.lng, lat: args.lngLat.lat },
    mapView: {
      zoom: args.zoom,
      bearing: args.bearing,
      pitch: args.pitch,
    },
    query: {
      pointPx: args.pointPx,
      bboxPadPx: args.bboxPadPx,
      queryRenderedFeaturesError: args.queryRenderedFeaturesError ?? null,
      renderedFeatureCount: dedupe.rawCount,
      nonPipelineLikeHitCount: nonPipelineHitCount,
      ...dedupe,
      clonedPipelineFeaturesForUi: selected.length,
      nextStepHint,
    },
    renderedFeatureSummaries:
      raw.length > PIPELINE_CLICK_DEBUG_SUMMARY_LIMIT
        ? raw.slice(0, PIPELINE_CLICK_DEBUG_SUMMARY_LIMIT).map((f, i) => renderedHitDebugSummary(f, i))
        : raw.map((f, i) => renderedHitDebugSummary(f, i)),
    renderedSummariesTruncated: raw.length > PIPELINE_CLICK_DEBUG_SUMMARY_LIMIT,
    renderedSummariesOmittedCount:
      raw.length > PIPELINE_CLICK_DEBUG_SUMMARY_LIMIT ? raw.length - PIPELINE_CLICK_DEBUG_SUMMARY_LIMIT : 0,
  })
}

/** Mobile `lineStyles.selectedFeature.lineColor` (#fee135). */
const PIPELINE_HIGHLIGHT_LINE_COLOR = '#fee135'
/** Mobile `lineStyles.feature.lineWidth` / `lineOpacity`. */
const PIPELINE_HIGHLIGHT_LINE_WIDTH = 12
const PIPELINE_HIGHLIGHT_LINE_OPACITY = 0.3

const vectorHighlightPaint: mapboxgl.LinePaint = {
  'line-color': PIPELINE_HIGHLIGHT_LINE_COLOR,
  'line-width': PIPELINE_HIGHLIGHT_LINE_WIDTH,
  'line-opacity': PIPELINE_HIGHLIGHT_LINE_OPACITY,
}

/**
 * Narrow filter for one segment. CO_NAME alone (e.g. "Williams") highlights every operator line
 * in the tile — use feature id, BUXUS_ID, and SYS_NAME (line name) like the mobile query box.
 */
function buildPipelineHighlightFilter(
  feature: Feature,
  hit: mapboxgl.GeoJSONFeature,
): mapboxgl.Expression | undefined {
  if (hit.id != null && hit.id !== '') {
    return ['==', ['id'], hit.id]
  }

  const props = (feature.properties ?? {}) as Record<string, unknown>
  const hp = (hit.properties ?? {}) as Record<string, unknown>
  const clauses: mapboxgl.Expression[] = []

  const wantId = props.BUXUS_ID ?? props.buxus_id ?? props.Id ?? hp.BUXUS_ID ?? hp.buxus_id ?? hp.Id
  if (wantId != null) {
    clauses.push([
      'any',
      ['==', ['get', 'BUXUS_ID'], wantId],
      ['==', ['get', 'buxus_id'], wantId],
      ['==', ['get', 'Id'], wantId],
    ])
  }

  const sysName = props.SYS_NAME ?? props.sys_name ?? hp.SYS_NAME ?? hp.sys_name
  if (typeof sysName === 'string' && sysName.trim() !== '') {
    clauses.push(['==', ['get', 'SYS_NAME'], sysName.trim()])
  }

  if (clauses.length === 0) {
    const wantCo = props.CO_NAME ?? props.co_name ?? hp.CO_NAME ?? hp.co_name
    if (wantCo != null) {
      clauses.push(['==', ['get', 'CO_NAME'], wantCo])
    }
  }

  if (clauses.length === 0) return undefined
  if (clauses.length === 1) return clauses[0]!
  return ['all', ...clauses]
}

function findHighlightQueryHit(
  features: Feature[],
  selectedIndex: number,
  queryHits: mapboxgl.GeoJSONFeature[],
): mapboxgl.GeoJSONFeature | null {
  const selected = features[selectedIndex] ?? features[0]
  if (!selected) return queryHits[0] ?? null
  const props = (selected.properties ?? {}) as Record<string, unknown>
  const wantId = props.BUXUS_ID ?? props.buxus_id ?? props.Id
  const wantCo = props.CO_NAME ?? props.co_name
  for (const hit of queryHits) {
    if (!featureHasPipelineProps(hit)) continue
    const hp = (hit.properties ?? {}) as Record<string, unknown>
    if (wantId != null && (hp.BUXUS_ID === wantId || hp.buxus_id === wantId || hp.Id === wantId)) {
      return hit
    }
    if (wantCo != null && (hp.CO_NAME === wantCo || hp.co_name === wantCo)) {
      return hit
    }
  }
  return queryHits.find(featureHasPipelineProps) ?? null
}

function queryHitVectorTarget(
  hit: mapboxgl.GeoJSONFeature,
): { source: string; sourceLayer: string } | null {
  const source = typeof hit.source === 'string' ? hit.source : undefined
  const sourceLayer = hit.sourceLayer
  if (!source || !sourceLayer) return null
  return { source, sourceLayer }
}

function removePipelineHighlightLayer(map: mapboxgl.Map): void {
  if (map.getLayer(HL_LAYER_VECTOR)) map.removeLayer(HL_LAYER_VECTOR)
  if (map.getLayer(HL_LAYER)) map.removeLayer(HL_LAYER)
  if (map.getSource(HL_SRC)) map.removeSource(HL_SRC)
}

function raisePipelineHighlightLayer(map: mapboxgl.Map): void {
  try {
    if (map.getLayer(HL_LAYER_VECTOR)) map.moveLayer(HL_LAYER_VECTOR)
    if (map.getLayer(HL_LAYER)) map.moveLayer(HL_LAYER)
  } catch {
    /* style may not be ready */
  }
}

function ensurePipelineHighlightLayer(map: mapboxgl.Map): boolean {
  if (!map.isStyleLoaded()) return false
  if (!map.getSource(HL_SRC)) {
    map.addSource(HL_SRC, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
  }
  if (!map.getLayer(HL_LAYER)) {
    const layerDef = {
      id: HL_LAYER,
      type: 'line' as const,
      source: HL_SRC,
      layout: {
        'line-join': 'round' as const,
        'line-cap': 'round' as const,
      },
      paint: {
        /* Mobile `lineStyles.feature` + selected/unselected lineColor */
        'line-width': PIPELINE_HIGHLIGHT_LINE_WIDTH,
        'line-opacity': PIPELINE_HIGHLIGHT_LINE_OPACITY,
        'line-color': [
          'case',
          ['boolean', ['get', '_selected'], false],
          PIPELINE_HIGHLIGHT_LINE_COLOR,
          'transparent',
        ] as mapboxgl.Expression,
      },
    }
    try {
      map.addLayer({ ...layerDef, slot: 'top' })
    } catch {
      map.addLayer(layerDef)
    }
  }
  raisePipelineHighlightLayer(map)
  return true
}

function clearPipelineHighlightData(map: mapboxgl.Map | null): void {
  if (!map) return
  removePipelineHighlightLayer(map)
}

/**
 * Mapbox GL JS: re-draw the selected segment from the same vector source/layer as the basemap
 * (filter by BUXUS_ID / CO_NAME). Native mobile instead uses `ShapeSource shape={feature}` because
 * `queryRenderedFeaturesInRect` returns full GeoJSON there — GL JS often does not.
 */
function applyVectorPipelineHighlight(
  map: mapboxgl.Map,
  feature: Feature,
  hit: mapboxgl.GeoJSONFeature,
): boolean {
  const target = queryHitVectorTarget(hit)
  const filter = buildPipelineHighlightFilter(feature, hit)
  if (!target || !filter) return false

  const layout = {
    'line-join': 'round' as const,
    'line-cap': 'round' as const,
  }

  if (map.getLayer(HL_LAYER_VECTOR)) {
    try {
      map.setFilter(HL_LAYER_VECTOR, filter)
      map.setPaintProperty(HL_LAYER_VECTOR, 'line-color', PIPELINE_HIGHLIGHT_LINE_COLOR)
      map.setPaintProperty(HL_LAYER_VECTOR, 'line-width', PIPELINE_HIGHLIGHT_LINE_WIDTH)
      map.setPaintProperty(HL_LAYER_VECTOR, 'line-opacity', PIPELINE_HIGHLIGHT_LINE_OPACITY)
      raisePipelineHighlightLayer(map)
      return true
    } catch {
      if (map.getLayer(HL_LAYER_VECTOR)) map.removeLayer(HL_LAYER_VECTOR)
    }
  }

  const layerDef: mapboxgl.LineLayerSpecification = {
    id: HL_LAYER_VECTOR,
    type: 'line',
    source: target.source,
    'source-layer': target.sourceLayer,
    filter,
    layout,
    paint: vectorHighlightPaint,
  }
  try {
    map.addLayer({ ...layerDef, slot: 'top' })
  } catch {
    try {
      map.addLayer(layerDef)
    } catch (e: unknown) {
      console.debug('[Buxus pipeline map] vector highlight layer failed', {
        error: e instanceof Error ? e.message : String(e),
        source: target.source,
        sourceLayer: target.sourceLayer,
      })
      return false
    }
  }
  raisePipelineHighlightLayer(map)
  return true
}

/** GeoJSON overlay fallback when vector-source highlight is unavailable. */
function applyGeoJsonPipelineHighlight(
  map: mapboxgl.Map,
  features: Feature[],
  selectedIndex: number,
  queryHits: mapboxgl.GeoJSONFeature[],
): boolean {
  if (!ensurePipelineHighlightLayer(map)) return false
  const src = map.getSource(HL_SRC) as mapboxgl.GeoJSONSource | undefined
  if (!src) return false

  const highlightFeatures = features.flatMap((feature, index) => {
    const withLine = enrichPipelineFeatureWithLineGeometry(feature, queryHits)
    if (!isLineGeometry(withLine.geometry)) return []
    return [
      {
        type: 'Feature' as const,
        properties: { _selected: index === selectedIndex },
        geometry: withLine.geometry,
      },
    ]
  })

  if (!highlightFeatures.length) return false
  src.setData({ type: 'FeatureCollection', features: highlightFeatures })
  raisePipelineHighlightLayer(map)
  return true
}

function applyPipelineHighlight(
  map: mapboxgl.Map | null,
  features: Feature[],
  selectedIndex: number,
  queryHits: mapboxgl.GeoJSONFeature[],
): void {
  if (!map) return

  const paintHighlight = (): void => {
    if (!map.isStyleLoaded()) {
      whenMapStyleReady(map, paintHighlight)
      return
    }

    if (!features.length) {
      clearPipelineHighlightData(map)
      return
    }

    const feature = features[selectedIndex] ?? features[0]!
    const hit = findHighlightQueryHit(features, selectedIndex, queryHits)

    if (hit && applyVectorPipelineHighlight(map, feature, hit)) {
      if (map.getLayer(HL_LAYER)) map.removeLayer(HL_LAYER)
      return
    }

    if (applyGeoJsonPipelineHighlight(map, features, selectedIndex, queryHits)) {
      if (map.getLayer(HL_LAYER_VECTOR)) map.removeLayer(HL_LAYER_VECTOR)
      return
    }

    console.debug('[Buxus pipeline map] highlight skipped — no vector target or line geometry', {
      featureCount: features.length,
      selectedIndex,
      queryHitCount: queryHits.length,
      queryHitSources: queryHits.map((h) => ({
        source: h.source ?? null,
        sourceLayer: h.sourceLayer ?? null,
        layerType: h.layer?.type ?? null,
        geometryType:
          (typeof h.toJSON === 'function' ? h.toJSON().geometry : h.geometry)?.type ?? null,
      })),
    })
    clearPipelineHighlightData(map)
  }

  whenMapStyleReady(map, paintHighlight)
}

function cloneFeatureGeometry(
  geom: mapboxgl.GeoJSONFeature['geometry'],
): Feature['geometry'] | undefined {
  if (!geom || typeof geom !== 'object') return undefined
  try {
    return JSON.parse(JSON.stringify(geom)) as Feature['geometry']
  } catch {
    return geom as Feature['geometry']
  }
}

/** Same de-dupe as mobile; store query hits for popup + highlight (ShapeSource parity). */
function cloneQueryFeatures(
  features: mapboxgl.GeoJSONFeature[],
  allQueryHits: mapboxgl.GeoJSONFeature[],
): Feature[] {
  const out: Feature[] = []
  const seen = new Set<string>()
  for (const f of features) {
    if (!featureHasPipelineProps(f)) continue
    const props = (f.properties ?? {}) as Record<string, unknown>
    const bid = props.BUXUS_ID ?? props.buxus_id ?? props.Id
    const serialized = enrichPipelineFeatureWithLineGeometry(
      pipelineFeatureFromQueryHit(f),
      allQueryHits,
    )
    const geom = serialized.geometry
    const geomKey =
      geom && typeof geom === 'object' && 'coordinates' in geom
        ? `${geom.type ?? '?'}:${JSON.stringify((geom as { coordinates: unknown }).coordinates)}`
        : f.id != null
          ? `fid:${String(f.id)}`
          : 'x'
    const key = bid != null && String(bid).trim() ? `id:${String(bid)}` : `${f.layer?.id ?? '?'}:${geomKey}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(serialized)
  }
  out.sort((a, b) => {
    const aLine = isLineGeometry(a.geometry) ? 1 : 0
    const bLine = isLineGeometry(b.geometry) ? 1 : 0
    return bLine - aLine
  })
  return out
}

export function Map({
  payload,
  stakeholderCode,
  activeAssetId = null,
  embedded = false,
  jurisdictionCountyStateKeys = null,
  mapRole = 'embeddedOperator',
  hazardMapView = null,
  mapViewOverride = null,
  pipelineOperatorDirectory,
  onViewPipelineOperator,
  showMapHeader = false,
  mapHeaderTitle,
  mapPicker = null,
  suppressOperatorFraming = false,
  operatorFramingLockRef,
  onMapInstance,
  onMapBackgroundClick,
  onPipelineOverlayActiveChange,
  analytics,
}: MapProps) {
  const token = useMemo(() => getToken(), [])
  const analyticsRef = useRef(analytics)
  analyticsRef.current = analytics
  const lastGeoPairsRef = useRef<readonly CountyStatePair[]>([])
  const pipelineInteractionsEnabled = mapRole !== 'hazardStorage'
  const showCustomControls = mapRole !== 'hazardStorage'

  const mapView = useMemo(() => {
    const base = resolveMapView(
      payload,
      stakeholderCode ?? null,
      activeAssetId,
      mapRole,
      mapRole === 'hazardStorage' ? hazardMapView ?? null : null,
    )
    if (mapViewOverride) {
      return { ...base, center: mapViewOverride.center, zoom: mapViewOverride.zoom }
    }
    return base
  }, [payload, stakeholderCode, activeAssetId, mapRole, hazardMapView, mapViewOverride])

  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const suppressOperatorFramingRef = useRef(suppressOperatorFraming)
  suppressOperatorFramingRef.current = suppressOperatorFraming
  const onMapInstanceRef = useRef(onMapInstance)
  onMapInstanceRef.current = onMapInstance
  const lastStyleRef = useRef<string | null>(mapView.styleUrl)
  const clickAbortRef = useRef<AbortController | null>(null)
  const pipelineInteractionsRef = useRef(pipelineInteractionsEnabled)
  pipelineInteractionsRef.current = pipelineInteractionsEnabled

  const [err, setErr] = useState<string | null>(null)
  const [mapLoadNotice, setMapLoadNotice] = useState<string | null>(null)
  const [mapStatus, setMapStatus] = useState<{ center: [number, number]; zoom: number } | null>(
    null,
  )
  const [pipelineFeatures, setPipelineFeatures] = useState<Feature[]>([])
  const [pipelineJurisdictionDenied, setPipelineJurisdictionDenied] = useState<{
    reason: Extract<PipelineTapGateResolution, { allowed: false }>['reason']
    geoPairs: CountyStatePair[]
  } | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pipelineFeatureIndex, setPipelineFeatureIndex] = useState(0)
  const [pipelineTapPoint, setPipelineTapPoint] = useState<{
    lng: number
    lat: number
  } | null>(null)
  const pipelineTapPointRef = useRef(pipelineTapPoint)
  pipelineTapPointRef.current = pipelineTapPoint
  const onMapBackgroundClickRef = useRef(onMapBackgroundClick)
  onMapBackgroundClickRef.current = onMapBackgroundClick
  const onPipelineOverlayActiveChangeRef = useRef(onPipelineOverlayActiveChange)
  onPipelineOverlayActiveChangeRef.current = onPipelineOverlayActiveChange
  const pipelineFeaturesRef = useRef(pipelineFeatures)
  const pipelineFeatureIndexRef = useRef(pipelineFeatureIndex)
  const pipelineQueryHitsRef = useRef<mapboxgl.GeoJSONFeature[]>([])
  pipelineFeaturesRef.current = pipelineFeatures
  pipelineFeatureIndexRef.current = pipelineFeatureIndex

  const pipelineOverlayActive =
    pipelineInteractionsEnabled &&
    (pipelineJurisdictionDenied !== null || pipelineFeatures.length > 0)

  useEffect(() => {
    onPipelineOverlayActiveChangeRef.current?.(pipelineOverlayActive)
  }, [pipelineOverlayActive, pipelineInteractionsEnabled])

  useLockPageScrollWhileOpen(pipelineOverlayActive)

  const syncPipelineHighlightFromState = useCallback((map: mapboxgl.Map | null) => {
    if (!map) return
    applyPipelineHighlight(
      map,
      pipelineFeaturesRef.current,
      pipelineFeatureIndexRef.current,
      pipelineQueryHitsRef.current,
    )
  }, [])

  const clearPipelineUi = useCallback((map: mapboxgl.Map | null, trackDismiss = false) => {
    if (trackDismiss && analyticsRef.current) {
      trackMapTapped({
        ...analyticsRef.current,
        result: 'dismissed',
        mapRole,
        ...(pipelineTapPointRef.current
          ? (analyticsGeoCoordPair(
              pipelineTapPointRef.current.lat,
              pipelineTapPointRef.current.lng,
            ) ?? {})
          : {}),
        locationCountyState: formatLocationCountyState(lastGeoPairsRef.current),
      })
    }
    pipelineFeaturesRef.current = []
    pipelineFeatureIndexRef.current = 0
    pipelineQueryHitsRef.current = []
    setPipelineFeatures([])
    setPipelineJurisdictionDenied(null)
    setPipelineTapPoint(null)
    setPipelineFeatureIndex(0)
    clearPipelineHighlightData(map ?? mapRef.current)
  }, [mapRole])

  useEffect(() => {
    if (!pipelineOverlayActive) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      clearPipelineUi(mapRef.current, true)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [pipelineOverlayActive, clearPipelineUi])

  const onPipelineFeatureIndexChange = useCallback((next: number) => {
    pipelineFeatureIndexRef.current = next
    setPipelineFeatureIndex(next)
    const map = mapRef.current
    if (map) {
      applyPipelineHighlight(
        map,
        pipelineFeaturesRef.current,
        next,
        pipelineQueryHitsRef.current,
      )
    }
  }, [])

  const onPipelineSegmentNavigate = useCallback(
    (nextIndex: number, direction: 'previous' | 'next') => {
      const features = pipelineFeaturesRef.current
      const feature = features[nextIndex]
      if (analyticsRef.current && feature) {
        trackMapPopupDisplayed({
          ...analyticsRef.current,
          popupType: 'pipeline',
          trigger: 'pagination',
          paginationDirection: direction,
          feature,
          segmentIndex: nextIndex,
          segmentCount: features.length,
          mapRole,
          ...(pipelineTapPoint
            ? (analyticsGeoCoordPair(pipelineTapPoint.lat, pipelineTapPoint.lng) ?? {})
            : {}),
          locationCountyState: formatLocationCountyState(lastGeoPairsRef.current),
        })
      }
      onPipelineFeatureIndexChange(nextIndex)
    },
    [mapRole, onPipelineFeatureIndexChange, pipelineTapPoint],
  )

  /** Mobile `handleClickViewOperator` clears popup/highlight before navigation. */
  const handleViewPipelineOperator = useCallback(
    (operatorId: string) => {
      if (analyticsRef.current) {
        trackMenuItemSelected({
          ...analyticsRef.current,
          component: 'MapPipelinePopup',
          menu: 'Pipeline segment',
          selection: 'View Operator',
        })
      }
      clearPipelineUi(mapRef.current)
      onViewPipelineOperator?.(operatorId)
    },
    [clearPipelineUi, onViewPipelineOperator],
  )

  const jurisdictionKeysRef = useRef(jurisdictionCountyStateKeys)
  jurisdictionKeysRef.current = jurisdictionCountyStateKeys

  useEffect(() => {
    clearPipelineUi(null)
    setMapLoadNotice(null)
    if (!token || !containerRef.current || !mapView.styleUrl) return

    mapboxgl.accessToken = token
    lastStyleRef.current = mapView.styleUrl

    let active: mapboxgl.Map | null = null

    try {
      mapRef.current?.remove()
      mapRef.current = null

      const map = new mapboxgl.Map({
        container: containerRef.current,
        style: mapView.styleUrl,
        center: mapView.center,
        zoom: mapView.zoom,
      })
      active = map
      mapRef.current = map
      onMapInstanceRef.current?.(map)

      let styleLoaded = false
      let loadIssueReported = false
      const reportLoadIssue = (
        cause: MapLoadFailureCause | 'offline_or_dns',
        error?: unknown,
        notice?: string,
      ): void => {
        if (loadIssueReported) return
        loadIssueReported = true
        const message =
          notice ??
          (cause === 'offline_or_dns'
            ? "Map couldn't load (offline or network). Menus and the asset list still work."
            : "Map couldn't load for this view. Menus and the asset list still work.")
        const styleCause: MapLoadFailureCause =
          cause === 'style_load_timeout' ? 'style_load_timeout' : 'style_error'
        const reportMessage =
          cause === 'offline_or_dns'
            ? FRIENDLY_CONNECTIVITY_MESSAGE
            : formatStyleLevelMapLoadFailureMessage({
                mapRole,
                styleUrl: mapView.styleUrl,
                cause: styleCause,
                error,
                timeoutMs: MAP_STYLE_LOAD_TIMEOUT_MS,
              })
        const err = error instanceof Error ? error : new Error(reportMessage)
        reportReliabilityFailure(err, {
          context: 'mapLoad',
          severity: 'warning',
          failureKind: cause === 'offline_or_dns' ? 'connectivity' : 'unknown',
          metadata: {
            map: {
              mapRole: mapRole ?? 'unknown',
              styleUrl: mapView.styleUrl ?? null,
              cause,
            },
          },
          analyticsEvent: AppEventTypes.MAP_LOAD_FAILED,
          analyticsProps: analyticsRef.current
            ? { ...analyticsRef.current, mapRole, message, cause }
            : { mapRole, message, cause },
        })
        setMapLoadNotice(message)
      }

      const styleLoadTimeout = window.setTimeout(() => {
        if (!styleLoaded && mapRef.current === map) reportLoadIssue('style_load_timeout')
      }, MAP_STYLE_LOAD_TIMEOUT_MS)

      const onMapError = (e: mapboxgl.ErrorEvent): void => {
        // Per-tile misses: log only, never Bugsnag. Style / offline-DNS: reliability report + banner.
        if (isPerTileMapLoadingError(e)) {
          logIntermittentMapTileLoadMiss(mapView.styleUrl, e.error ?? e)
          return
        }
        if (isOfflineOrDnsMapLoadingError(e.error ?? e)) {
          reportLoadIssue('offline_or_dns', e.error)
          return
        }
        if (!isStyleLevelMapLoadingError(e.error ?? e)) {
          logIntermittentMapTileLoadMiss(mapView.styleUrl, e.error)
          return
        }
        reportLoadIssue('style_error', e.error)
      }

      map.once('style.load', () => {
        styleLoaded = true
        window.clearTimeout(styleLoadTimeout)
        setMapLoadNotice(null)
      })

      map.on('error', onMapError)

      if (!showCustomControls) {
        map.addControl(new mapboxgl.NavigationControl({ showCompass: true }), 'top-right')
      }

      if (showMapHeader) {
        const syncMapStatus = (): void => {
          const c = map.getCenter()
          setMapStatus({ center: [c.lng, c.lat], zoom: map.getZoom() })
        }
        map.on('moveend', syncMapStatus)
        map.on('zoomend', syncMapStatus)
        map.once('load', syncMapStatus)
      }

      const bindInteractions = (): void => {
        if (!pipelineInteractionsRef.current) return

        const onClick = (ev: mapboxgl.MapMouseEvent) => {
          clickAbortRef.current?.abort()
          const ac = new AbortController()
          clickAbortRef.current = ac

          clearPipelineUi(map)

          const { lngLat } = ev
          const centerPx = map.project(lngLat)
          const pad = PIPELINE_QUERY_BUFFER_METERS

          let raw: mapboxgl.GeoJSONFeature[] = []
          let queryRenderedFeaturesError: string | null = null
          try {
            raw = queryPipelineRenderedFeatures(map, lngLat)
          } catch (e: unknown) {
            queryRenderedFeaturesError = e instanceof Error ? e.message : String(e)
            raw = []
          }

          pipelineQueryHitsRef.current = raw.filter(featureHasPipelineProps)
          const selected = cloneQueryFeatures(raw, pipelineQueryHitsRef.current)

          logPipelineMapClickDebug({
            mapRole,
            lngLat,
            zoom: map.getZoom(),
            bearing: map.getBearing(),
            pitch: map.getPitch(),
            pointPx: { x: centerPx.x, y: centerPx.y },
            bboxPadPx: pad,
            renderedFeatures: raw,
            clonedPipelineFeatures: selected,
            queryRenderedFeaturesError,
          })

          if (!selected.length) {
            const emptyStats = pipelineQueryDedupeStats(raw)
            console.debug('[Buxus pipeline map] no pipeline features to open (early exit)', {
              mapRole,
              ...emptyStats,
              clonedPipelineFeaturesForUi: 0,
            })
            if (analyticsRef.current) {
              trackMapTapped({
                ...analyticsRef.current,
                result: 'no_feature',
                mapRole,
                ...(analyticsGeoCoordPair(lngLat.lat, lngLat.lng) ?? {}),
              })
            }
            onMapBackgroundClickRef.current?.()
            return
          }

          const lng = lngLat.lng
          const lat = lngLat.lat

          void (async () => {
            try {
              const geoPairs = await fetchCountyStatePairsReverseMapboxLngLat({
                longitude: lng,
                latitude: lat,
                accessToken: token,
                signal: ac.signal,
              })

              const regBlob = readRegistrationProfileBlob()
              const gate = resolvePipelineMapTapAllowed({
                registrationProfileData: regBlob,
                jurisdictionCountyStateKeys: jurisdictionKeysRef.current,
                geoPairs,
              })

              if (!gate.allowed) {
                const profile = parseProfileJurisdiction(regBlob)
                const stored = jurisdictionKeysRef.current
                const storedCount = stored?.size ?? 0
                const profileRestrictedCount = profile.kind === 'restricted' ? profile.keys.size : 0
                const effectiveKeySource: 'localStorage' | 'registrationProfile' | 'none' =
                  storedCount > 0
                    ? 'localStorage'
                    : profile.kind === 'restricted' && profileRestrictedCount > 0
                      ? 'registrationProfile'
                      : 'none'
                const effectiveKeyCount =
                  storedCount > 0 ? storedCount : profile.kind === 'restricted' ? profileRestrictedCount : 0

                const jurisdictionKeysFromLocalStorage = jurisdictionKeysSorted(stored ?? null)
                const jurisdictionKeysFromProfile =
                  profile.kind === 'restricted' ? jurisdictionKeysSorted(profile.keys) : []
                const effectiveSet =
                  storedCount > 0
                    ? stored
                    : profile.kind === 'restricted'
                      ? profile.keys
                      : null
                const effectiveJurisdictionKeys = jurisdictionKeysSorted(effectiveSet)

                lastGeoPairsRef.current = geoPairs

                logPipelineTapDeniedDebug({
                  reason: gate.reason,
                  lngLat: { lng, lat },
                  geoPairs: [...geoPairs],
                  profileKind: profile.kind,
                  storedKeyCount: storedCount,
                  profileRestrictedKeyCount: profileRestrictedCount,
                  effectiveKeyCount,
                  effectiveKeySource,
                  jurisdictionKeysFromLocalStorage,
                  jurisdictionKeysFromProfile,
                  effectiveJurisdictionKeys,
                })

                if (PIPELINE_MAP_SHOW_JURISDICTION_DENIED_POPUP) {
                  if (analyticsRef.current) {
                    trackMapTapped({
                      ...analyticsRef.current,
                      result: 'jurisdiction_denied',
                      denyReason: gate.reason,
                      mapRole,
                      ...(analyticsGeoCoordPair(lat, lng) ?? {}),
                      locationCountyState: formatLocationCountyState(geoPairs),
                    })
                  }
                  setPipelineJurisdictionDenied({
                    reason: gate.reason,
                    geoPairs: [...geoPairs],
                  })
                  pipelineQueryHitsRef.current = []
                }
                return
              }

              setPipelineJurisdictionDenied(null)

              lastGeoPairsRef.current = geoPairs

              if (analyticsRef.current) {
                trackMapTapped({
                  ...analyticsRef.current,
                  result: 'pipeline_shown',
                  segmentCount: selected.length,
                  mapRole,
                  ...(analyticsGeoCoordPair(lat, lng) ?? {}),
                  locationCountyState: formatLocationCountyState(geoPairs),
                })
                trackMapPopupDisplayed({
                  ...analyticsRef.current,
                  popupType: 'pipeline',
                  trigger: 'map_tap',
                  feature: selected[0],
                  segmentIndex: 0,
                  segmentCount: selected.length,
                  mapRole,
                  ...(analyticsGeoCoordPair(lat, lng) ?? {}),
                  locationCountyState: formatLocationCountyState(geoPairs),
                })
              }

              console.debug('[Buxus pipeline map] tap allowed — opening pipeline popup', {
                mapRole,
                lngLat: { lng, lat },
                reverseGeocodeCountyStatePairs: [...geoPairs],
                gateAllowed: true,
                clonedPipelineFeatureCount: selected.length,
                firstHitPipelineKeysPreview: selected[0]
                  ? previewPipelineRelatedProps((selected[0].properties ?? {}) as Record<string, unknown>)
                  : null,
              })

              setPipelineFeatures(selected)
              setPipelineFeatureIndex(0)
              setPipelineTapPoint({ lng, lat })
              pipelineFeaturesRef.current = selected
              pipelineFeatureIndexRef.current = 0
              applyPipelineHighlight(map, selected, 0, pipelineQueryHitsRef.current)
            } catch (e: unknown) {
              if (ac.signal.aborted) {
                console.debug('[Buxus pipeline map] tap async aborted', {
                  mapRole,
                  lngLat: { lng, lat },
                })
                return
              }
              console.debug('[Buxus pipeline map] tap async error', {
                mapRole,
                lngLat: { lng, lat },
                error:
                  e instanceof Error
                    ? { name: e.name, message: e.message, stack: e.stack }
                    : { message: String(e) },
              })
              reportOperationalFailure(e, {
                context: 'mapPipelineReverseGeocode',
                metadata: {
                  map: { mapRole: mapRole ?? 'unknown' },
                },
              })
            }
          })()
        }

        map.on('click', onClick)

        map.once('remove', () => {
          map.off('click', onClick)
        })
      }

      map.on('style.load', () => {
        const m = mapRef.current
        if (!m || m !== map) return
        try {
          removePipelineHighlightLayer(m)
          syncPipelineHighlightFromState(m)
        } catch {
          /* non-fatal */
        }
      })

      map.on('load', () => {
        bindInteractions()
      })

      map.once('remove', () => {
        window.clearTimeout(styleLoadTimeout)
        map.off('error', onMapError)
      })
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e)
      reportReliabilityFailure(e instanceof Error ? e : new Error(message), {
        context: 'mapInit',
        severity: 'warning',
        analyticsEvent: AppEventTypes.MAP_LOAD_FAILED,
        analyticsProps: {
          mapRole,
          message,
          cause: 'map_constructor',
        },
      })
      queueMicrotask(() => setErr(message))
    }

    return () => {
      clickAbortRef.current?.abort()
      onMapInstanceRef.current?.(null)
      active?.remove()
      mapRef.current = null
      setMapStatus(null)
      setMapLoadNotice(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- framing updates use the flyTo effect below; avoid discarding the Map on each center/zoom change
  }, [
    clearPipelineUi,
    syncPipelineHighlightFromState,
    mapRole,
    mapView.styleUrl,
    token,
    pipelineInteractionsEnabled,
    showCustomControls,
  ])

  useEffect(() => {
    const map = mapRef.current
    if (!map || mapView.styleUrl !== lastStyleRef.current) return
    if (!map.loaded()) return
    if (operatorFramingLockRef?.current || suppressOperatorFramingRef.current) return
    map.flyTo({ center: mapView.center, zoom: mapView.zoom, essential: true })
  }, [mapView.center, mapView.zoom, mapView.styleUrl])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    syncPipelineHighlightFromState(map)
  }, [pipelineFeatures, pipelineFeatureIndex, syncPipelineHighlightFromState])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      mapRef.current?.resize()
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const handleZoomIn = useCallback(() => {
    if (analyticsRef.current) {
      trackButtonPressed({ ...analyticsRef.current, button: 'Zoom in' })
    }
    const map = mapRef.current
    if (!map) return
    map.zoomTo(Math.min(map.getZoom() + 1, 20), { duration: 300 })
  }, [])

  const handleZoomOut = useCallback(() => {
    if (analyticsRef.current) {
      trackButtonPressed({ ...analyticsRef.current, button: 'Zoom out' })
    }
    const map = mapRef.current
    if (!map) return
    map.zoomTo(Math.max(map.getZoom() - 1, 0), { duration: 300 })
  }, [])

  const handleCenterMap = useCallback(() => {
    if (analyticsRef.current) {
      trackButtonPressed({ ...analyticsRef.current, button: 'Center map' })
    }
    const map = mapRef.current
    if (!map) return
    map.flyTo({
      center: mapView.center,
      zoom: mapView.zoom,
      essential: true,
      duration: 2000,
    })
  }, [mapView.center, mapView.zoom])

  const handleOpenSearch = useCallback(() => {
    if (analyticsRef.current) {
      trackButtonPressed({ ...analyticsRef.current, button: 'Search address' })
    }
    setSearchOpen(true)
  }, [])

  const handleSearchSubmitted = useCallback(
    (payload: { result: MapAddressSearchResult; queryLength: number }) => {
      if (!analyticsRef.current) return
      trackMapAddressSearchSubmitted({
        ...analyticsRef.current,
        mapRole,
        ...payload,
      })
    },
    [mapRole],
  )

  const handleSearchResult = useCallback((center: [number, number]) => {
    mapRef.current?.flyTo({ center, zoom: 16, essential: true, duration: 2000 })
  }, [])

  const handlePickerSelect = useCallback(
    (id: string) => {
      mapPicker?.onSelect(id)
      setPickerOpen(false)
    },
    [mapPicker],
  )

  const pickerOverlayConfig = useMemo((): MapPickerConfig | null => {
    if (!mapPicker?.items.length) return null
    return {
      ...mapPicker,
      onSelect: handlePickerSelect,
    }
  }, [handlePickerSelect, mapPicker])

  if (err) {
    return (
      <div className="map-note">
        <h2>Map</h2>
        <p>Failed to initialize Mapbox: {err}</p>
      </div>
    )
  }

  if (!mapView.styleUrl && mapRole !== 'hazardStorage') {
    if (embedded) {
      return (
        <section className="map-shell" aria-label="Map">
          <div className="map-stage map-stage--custom-controls">
            <div className="map-container embedded map-container--unavailable" />
            <MapLoadNotice message="Map isn't configured for this view. Menus and the asset list still work." />
          </div>
        </section>
      )
    }
    return (
      <div className="map-note">
        <h2>Map</h2>
        <p>Map is not configured for this view.</p>
      </div>
    )
  }

  const resolvedMapHeaderTitle =
    mapHeaderTitle ?? (mapRole === 'explorerHome' ? 'Pipeline map' : 'Map')
  const statusCenter = mapStatus?.center ?? mapView.center
  const statusZoom = mapStatus?.zoom ?? mapView.zoom

  const showHeaderBlock = showMapHeader

  return (
    <section
      className={[
        'map-shell',
        mapRole === 'explorerHome' ? 'map-shell-explorer' : '',
        showHeaderBlock ? 'map-shell--with-header' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label="Map"
    >
      {showHeaderBlock ? (
        <div className="map-header">
          <h2 className="map-title">{resolvedMapHeaderTitle}</h2>
          <p className="map-subtitle">
            center [{statusCenter[1].toFixed(5)}, {statusCenter[0].toFixed(5)}] · zoom{' '}
            {statusZoom.toFixed(2)}
          </p>
        </div>
      ) : null}
      <div
        className={['map-stage', showCustomControls ? 'map-stage--custom-controls' : '']
          .filter(Boolean)
          .join(' ')}
      >
        <div
          ref={containerRef}
          className={
            embedded
              ? mapRole === 'hazardStorage'
                ? 'map-container embedded map-hazard-storage'
                : 'map-container embedded'
              : 'map-container'
          }
        />
        {showCustomControls ? (
          <MapCustomControls
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onSearch={handleOpenSearch}
            onCenter={handleCenterMap}
            onOpenPicker={
              pickerOverlayConfig && pickerOverlayConfig.items.length > 0
                ? () => setPickerOpen(true)
                : undefined
            }
            pickerAriaLabel={mapPicker?.title ?? 'Operators'}
            pickerIconPath={mapPicker?.pickerIconPath}
          />
        ) : null}
        {searchOpen ? (
          <MapAddressSearchModal
            onClose={() => setSearchOpen(false)}
            onResult={handleSearchResult}
            onSearchSubmitted={handleSearchSubmitted}
          />
        ) : null}
        {pickerOpen && pickerOverlayConfig ? (
          <MapPickerOverlay config={pickerOverlayConfig} onClose={() => setPickerOpen(false)} />
        ) : null}
        {mapLoadNotice ? <MapLoadNotice message={mapLoadNotice} /> : null}
        {pipelineInteractionsEnabled &&
        (pipelineJurisdictionDenied || pipelineFeatures.length > 0) ? (
          <div
            className={[
              'map-pipeline-overlay-panel',
              pipelineJurisdictionDenied
                ? 'map-pipeline-overlay-panel--denied'
                : 'map-pipeline-overlay-panel--segment',
            ].join(' ')}
            aria-live="polite"
          >
            {pipelineJurisdictionDenied ? (
              <MapPipelineJurisdictionDeniedBubble
                reason={pipelineJurisdictionDenied.reason}
                geoPairs={pipelineJurisdictionDenied.geoPairs}
                showLocation={PIPELINE_MAP_JURISDICTION_DENIED_SHOW_LOCATION}
                onClose={() => clearPipelineUi(mapRef.current, true)}
              />
            ) : (
              <MapPipelineBubble
                features={pipelineFeatures}
                selectedIndex={pipelineFeatureIndex}
                tapPoint={pipelineTapPoint}
                onSegmentNavigate={onPipelineSegmentNavigate}
                onClose={() => clearPipelineUi(mapRef.current, true)}
                onViewOperator={
                  onViewPipelineOperator ? handleViewPipelineOperator : undefined
                }
                operatorDirectory={pipelineOperatorDirectory}
                analytics={analytics}
                mapRole={mapRole}
              />
            )}
          </div>
        ) : null}
      </div>
    </section>
  )
}
